import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { EnhancedAvatar } from '@/components/photos/EnhancedAvatar';
import { LazyImage } from '@/components/ui/lazy-image';
import { PhotoZoomModal } from '@/components/photos/PhotoZoomModal';
import { usePhotoZoom } from '@/hooks/usePhotoZoom';
import { Progress } from '@/components/ui/progress';
import { PresenceIndicator } from '@/components/presence/PresenceIndicator';
import { ProductInterestsList } from '@/components/members/ProductInterestsList';
import { useProductInterests } from '@/hooks/useProductInterests';
import { 
  MapPin, Briefcase, Languages, Clock, Star, 
  MessageCircle, Eye, Activity, Users, ArrowRight,
  Heart, Share2, Zap, TrendingUp, Award, Brain,
  Check, UserCheck, UserX, CheckCircle, XCircle, QrCode,
  Search, Gift, Send
} from 'lucide-react';
import { OtherMemberCardModal } from '../directory/OtherMemberCardModal';
import { Member } from '@/types/member';
import { SmartTag, ContextualIcon, MemberContext } from '@/types/search';
import { ContextualSearchEngine } from '@/services/contextualSearch';
import { cn } from '@/lib/utils';
import { formatMemberRoleByType } from '@/lib/userDisplayUtils';
import { EventInvitationButton } from '@/components/events/EventInvitationButton';
import { UserStatus } from '@/types/presence';
import { MemberTypeBadge } from '@/components/ui/member-type-badge';
import { MemberTypeBadgesLocal } from '@/components/admin/members/MemberTypeBadgesLocal';
import { TeamMemberBadge } from '@/components/ui/team-member-badge';
import { useProfileViews } from '@/hooks/useProfileViews';
import { usePermissions } from '@/hooks/usePermissions';
import { HotLeadIndicators } from './HotLeadIndicators';
import { MatchedSkillsBadges } from './MatchedSkillsBadges';
import { getContextualHook } from '@/utils/memberHooks';
import { SuggestProfileDialog } from '@/components/networking/SuggestProfileDialog';

interface SmartTaggedMemberCardProps {
  member: Member;
  viewMode: 'grid' | 'list';
  currentUserId?: string;
  currentUserMember?: Member;
  onViewProfile: (member: Member) => void;
  onMessage: (member: Member) => void;
  onConnect: (member: Member) => void;
  onAccept?: (member: Member) => void;
  onDecline?: (member: Member) => void;
  onFavorite: (member: Member) => void;
  onShare: (member: Member) => void;
  userStatus?: UserStatus;
  compatibilityScore?: number;
  mutualConnections?: number;
  lastActive?: string;
  searchTerm?: string;
  allMembers?: Member[];
  connectionStatus?: string | null;
}

export const SmartTaggedMemberCard: React.FC<SmartTaggedMemberCardProps> = ({
  member,
  viewMode,
  currentUserId,
  currentUserMember,
  onViewProfile,
  onMessage,
  onConnect,
  onAccept,
  onDecline,
  onFavorite,
  onShare,
  userStatus = 'offline',
  compatibilityScore = 0,
  mutualConnections = 0,
  lastActive = undefined,
  searchTerm = '',
  allMembers = [],
  connectionStatus = null
}) => {
  const [isFavorited, setIsFavorited] = useState(false);
  const [memberContext, setMemberContext] = useState<MemberContext | null>(null);
  const [hovering, setHovering] = useState(false);
  const [isQRModalOpen, setIsQRModalOpen] = useState(false);
  const [isSendingRequest, setIsSendingRequest] = useState(false);
  const [isSuggestDialogOpen, setIsSuggestDialogOpen] = useState(false);
  
  const isCurrentUser = currentUserId && (member.user_id === currentUserId || member.id === currentUserId);
  
  const { useProfileViewCount } = useProfileViews();
  const { hasPermission } = usePermissions();
  const { data: profileViews = 0 } = useProfileViewCount(member.id);

  // Hook contextuel pour le membre
  const contextualHook = getContextualHook(member);

  // Photo zoom functionality
  const { isZoomOpen, selectedMember, currentIndex, openZoom, closeZoom, navigateToNext, navigateToPrev } = usePhotoZoom(allMembers);

  // Product interests functionality
  const { data: productInterests = [], isLoading: interestsLoading } = useProductInterests(member.id);

  // Générer le contexte intelligent du membre
  useEffect(() => {
    if (searchTerm && allMembers.length > 0) {
      const context = ContextualSearchEngine.generateMemberContext(member, searchTerm, allMembers);
      setMemberContext(context);
    }
  }, [member, searchTerm, allMembers]);

  const handleFavorite = () => {
    setIsFavorited(!isFavorited);
    onFavorite(member);
  };

  const handleConnect = async () => {
    setIsSendingRequest(true);
    try {
      await onConnect(member);
    } finally {
      setIsSendingRequest(false);
    }
  };

  // Get connect button props based on connection status
  const getConnectButtonProps = () => {
    switch (connectionStatus) {
      case 'pending':
        return {
          text: 'Envoyé',
          icon: Clock,
          disabled: true,
          variant: 'outline' as const,
          className: 'opacity-75 min-w-0'
        };
      case 'accepted':
        return {
          text: 'Se déconnecter',
          icon: UserX,
          disabled: false,
          variant: 'outline' as const,
          className: 'text-foreground border-border hover:bg-destructive hover:text-destructive-foreground min-w-0'
        };
      case 'incoming':
        return {
          showTwoButtons: true,
          acceptButton: {
            text: '✓',
            icon: CheckCircle,
            onClick: () => onAccept?.(member),
            variant: 'default' as const,
            className: 'bg-accent text-accent-foreground hover:bg-accent/90 min-w-0'
          },
          declineButton: {
            text: '✗',
            icon: XCircle,
            onClick: () => onDecline?.(member),
            variant: 'outline' as const,
            className: 'text-red-600 border-red-200 hover:bg-red-50 min-w-0'
          }
        };
      case 'declined':
        return {
          text: 'Connecter',
          icon: Users,
          disabled: false,
          variant: 'default' as const,
          className: ''
        };
      default:
        return {
          text: 'Connecter',
          icon: Users,
          disabled: false,
          variant: 'default' as const,
          className: ''
        };
    }
  };

  const getTagColorClasses = (tag: SmartTag) => {
    const colorMap = {
      red: 'bg-destructive/10 text-destructive border-destructive/20',
      yellow: 'bg-primary/10 text-primary border-primary/20',
      green: 'bg-accent/10 text-accent-foreground border-accent/20',
      blue: 'bg-accent/10 text-accent-foreground border-accent/20',
      purple: 'bg-secondary/10 text-secondary-foreground border-secondary/20',
      indigo: 'bg-secondary/10 text-secondary-foreground border-secondary/20',
      pink: 'bg-secondary/10 text-secondary-foreground border-secondary/20',
      gray: 'bg-muted text-foreground border-border',
    };
    return colorMap[tag.color as keyof typeof colorMap] || colorMap.gray;
  };

  const renderSmartTags = () => {
    if (!memberContext?.smartTags.length) return null;

    return (
      <div className="flex flex-wrap gap-1">
        {memberContext.smartTags.slice(0, 2).map((tag) => (
          <Badge 
            key={tag.id}
            variant="outline"
            className={cn(
              "text-xs font-medium",
              getTagColorClasses(tag)
            )}
            title={tag.description}
          >
            <span className="mr-1">{tag.icon}</span>
            {tag.label}
            {tag.score > 0 && (
              <span className="ml-1 opacity-60">
                {Math.round(tag.score)}
              </span>
            )}
          </Badge>
        ))}
      </div>
    );
  };

  const renderContextualIcons = () => {
    if (!memberContext?.contextualIcons.length) return null;

    return (
      <div className="flex items-center gap-1">
        {memberContext.contextualIcons.map((icon, index) => (
          <Badge 
            key={index}
            variant="outline"
            className="text-xs p-1 h-6 w-6 rounded-full border-2"
            title={icon.label}
          >
            <span className="text-sm">{icon.icon}</span>
          </Badge>
        ))}
      </div>
    );
  };

  const renderHoverCard = () => {
    if (!hovering || !memberContext) return null;

    return (
      <Card className="absolute top-full left-0 right-0 z-50 mt-2 border shadow-xl bg-background/95 backdrop-blur-sm">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <div className="text-muted-foreground mb-1">Taux de réponse</div>
              <div className="font-semibold flex items-center gap-1">
                <Zap className="h-3 w-3 text-accent-foreground" />
                {Math.round(memberContext.responseRate)}%
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Popularité</div>
              <div className="font-semibold flex items-center gap-1">
                <TrendingUp className="h-3 w-3 text-primary" />
                {Math.round(memberContext.popularityScore)}%
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Dernière activité</div>
              <div className="font-semibold text-accent-foreground">
                {memberContext.lastActivity.toLocaleDateString('fr-FR')}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground mb-1">Score de pertinence</div>
              <div className="font-semibold flex items-center gap-1">
                <Brain className="h-3 w-3 text-purple-500" />
                {Math.round(memberContext.relevanceScore)}%
              </div>
            </div>
          </div>

          {/* Actions rapides */}
          <div className="flex gap-2 pt-2 border-t">
            <Button variant="outline" size="sm" className="flex-1" onClick={() => onMessage(member)}>
              <MessageCircle className="h-4 w-4 mr-1" />
              Message
            </Button>
                     {(() => {
                       const buttonProps = getConnectButtonProps();
                       
                       if (buttonProps.showTwoButtons) {
                         const AcceptIcon = buttonProps.acceptButton.icon;
                         const DeclineIcon = buttonProps.declineButton.icon;
                         return (
                           <div className="flex gap-1 flex-1">
                             <Button 
                               variant={buttonProps.acceptButton.variant} 
                               size="sm" 
                               className={cn("flex-1", buttonProps.acceptButton.className)}
                               onClick={buttonProps.acceptButton.onClick}
                             >
                               <AcceptIcon className="h-3 w-3 mr-1" />
                               {buttonProps.acceptButton.text}
                             </Button>
                             <Button 
                               variant={buttonProps.declineButton.variant} 
                               size="sm" 
                               className={cn("flex-1", buttonProps.declineButton.className)}
                               onClick={buttonProps.declineButton.onClick}
                             >
                               <DeclineIcon className="h-3 w-3 mr-1" />
                               {buttonProps.declineButton.text}
                             </Button>
                           </div>
                         );
                       }
                       
                        const IconComponent = buttonProps.icon;
                        return (
                          <Button 
                            variant={buttonProps.variant} 
                            size="sm" 
                            className={cn("flex-1", buttonProps.className)}
                            onClick={handleConnect}
                            disabled={buttonProps.disabled || isSendingRequest}
                            title={buttonProps.disabled ? buttonProps.text : undefined}
                          >
                            {isSendingRequest ? (
                              <>
                                <Clock className="h-4 w-4 mr-1 animate-spin" />
                                Envoi...
                              </>
                            ) : (
                              <>
                                <IconComponent className="h-4 w-4 mr-1" />
                                {buttonProps.text}
                              </>
                            )}
                          </Button>
                        );
                     })()}
          </div>
        </CardContent>
      </Card>
    );
  };

  if (viewMode === 'list') {
    return (
      <div 
        className="relative"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
      >
        <Card className="hover:shadow-md transition-all duration-300 border-l-4 border-l-transparent hover:border-l-primary">
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              {/* Avatar avec statut */}
              <div className="relative">
                <EnhancedAvatar
                  member={member}
                  size="md"
                  onClick={() => openZoom(member, allMembers, allMembers?.findIndex(m => m.id === member.id))}
                  onMessage={onMessage}
                  onConnect={onConnect}
                />
                {userStatus !== 'offline' && (
                  <div className="absolute bottom-0 right-0 h-4 w-4 rounded-full border-2 border-background bg-green-500" />
                )}
                
                {/* Bouton QR - positionné sous le badge de statut */}
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="absolute -bottom-2 -right-2 h-6 w-6 p-0 bg-white hover:bg-white/90 shadow-sm rounded-full border border-border"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsQRModalOpen(true);
                  }}
                  title="Voir la carte QR"
                >
                  <QrCode className="h-3 w-3 text-black" />
                </Button>
                
                {/* Icônes contextuelles mini */}
                <div className="absolute -top-1 -right-1">
                  {renderContextualIcons()}
                </div>
              </div>

              {/* Informations membre */}
              <div className="flex-1 space-y-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-lg text-black">{member.name}</h3>
                    {member.is_team_member && currentUserId && <TeamMemberBadge size="sm" />}
                    <MemberTypeBadgesLocal types={member.member_types || []} />
                  </div>
                  
                  {/* Badge statut en haut à droite */}
                  <div className="flex-shrink-0">
                    <PresenceIndicator 
                      status={userStatus} 
                      size="sm" 
                      showLabel={true}
                    />
                  </div>
                </div>
                
                {/* RÔLE/POSTE en évidence */}
                <p className="text-sm text-muted-foreground font-medium">
                  {formatMemberRoleByType(member)}
                </p>
                
                {/* Hook contextuel - seulement si différent du rôle */}
                {contextualHook !== formatMemberRoleByType(member) && (
                  <p className="text-xs text-foreground">
                    {contextualHook}
                  </p>
                )}
                
                <div className="flex items-center gap-4 text-sm text-black">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-4 w-4" />
                    <span>{member.country}</span>
                  </div>
                  {hasPermission('view_analytics') && (
                    <div className="flex items-center gap-1">
                      <Eye className="h-4 w-4" />
                      <span>{profileViews} vues</span>
                    </div>
                  )}
                </div>
                
                {/* Hot Lead Indicators - APRÈS */}
                <HotLeadIndicators 
                  member={member}
                  userStatus={userStatus}
                  compatibilityScore={compatibilityScore}
                />

                {/* Compétences matchées - seulement si pertinent */}
                {(currentUserMember?.skills?.length || currentUserMember?.needs?.length) && (
                  <MatchedSkillsBadges 
                    currentUserSkills={currentUserMember?.skills}
                    currentUserNeeds={currentUserMember?.needs}
                    memberSkills={member.skills}
                    memberNeeds={member.needs}
                    maxVisible={2}
                  />
                )}
                
                {/* Social Proof compact */}
                <div className="flex gap-3 text-xs text-muted-foreground">
                  {mutualConnections > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" />
                      {mutualConnections} connexions communes
                    </span>
                  )}
                  
                  {hasPermission('view_analytics') && profileViews > 0 && (
                    <span className="flex items-center gap-1">
                      <Eye className="w-3 h-3" />
                      {profileViews} vues
                    </span>
                  )}
                </div>

                {/* Product Interests preview */}
                {!interestsLoading && productInterests.length > 0 && (
                  <ProductInterestsList 
                    interests={productInterests}
                    maxVisible={2}
                    compact={true}
                  />
                )}

                {/* Ce que le membre RECHERCHE */}
                {member.needs && member.needs.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                      <Search className="h-3.5 w-3.5" />
                      <span>Recherche</span>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {member.needs.slice(0, 3).map((need, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs px-2 py-1 bg-yellow-100 text-yellow-900 border-yellow-300 font-medium">
                          {need}
                        </Badge>
                      ))}
                      {member.needs.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{member.needs.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}

                {/* Ce que le membre PROPOSE */}
                {member.offers && member.offers.length > 0 && (
                  <div className="space-y-1">
                    <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                      <Gift className="h-3.5 w-3.5" />
                      <span>Propose</span>
                    </div>
                    <div className="flex gap-1 flex-wrap">
                      {member.offers.slice(0, 3).map((offer, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs px-2 py-1 bg-yellow-100 text-yellow-900 border-yellow-300 font-medium">
                          {offer}
                        </Badge>
                      ))}
                      {member.offers.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{member.offers.length - 3}
                        </Badge>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Actions rapides */}
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="sm" onClick={handleFavorite}>
                  <Heart className={cn("h-4 w-4", isFavorited && "fill-red-500 text-red-500")} />
                </Button>
                <Button variant="ghost" size="sm" onClick={() => onShare(member)}>
                  <Share2 className="h-4 w-4" />
                </Button>
              {currentUserId && !isCurrentUser && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setIsSuggestDialogOpen(true)}
                  className="gap-1.5"
                  title="Proposer ce profil à quelqu'un"
                >
                  <Send className="h-3.5 w-3.5" />
                  <span className="text-xs">Proposer</span>
                </Button>
              )}
                {!isCurrentUser && (
                  <>
                    <Button variant="outline" size="sm" onClick={() => onMessage(member)}>
                      <MessageCircle className="h-4 w-4 mr-1" />
                      Message
                    </Button>
                    <EventInvitationButton
                      memberId={member.id}
                      memberName={member.name}
                      size="sm"
                      variant="ghost"
                    />
                     {(() => {
                       const buttonProps = getConnectButtonProps();
                       
                       if (buttonProps.showTwoButtons) {
                         const AcceptIcon = buttonProps.acceptButton.icon;
                         const DeclineIcon = buttonProps.declineButton.icon;
                         return (
                            <div className="flex gap-1">
                              <Button 
                                variant={buttonProps.acceptButton.variant} 
                                size="sm" 
                                className={cn("text-xs px-2", buttonProps.acceptButton.className)}
                                onClick={buttonProps.acceptButton.onClick}
                              >
                                <AcceptIcon className="h-3 w-3 mr-1" />
                                {buttonProps.acceptButton.text}
                              </Button>
                              <Button 
                                variant={buttonProps.declineButton.variant} 
                                size="sm" 
                                className={cn("text-xs px-2", buttonProps.declineButton.className)}
                                onClick={buttonProps.declineButton.onClick}
                              >
                                <DeclineIcon className="h-3 w-3 mr-1" />
                                {buttonProps.declineButton.text}
                              </Button>
                            </div>
                         );
                       }
                       
                        const IconComponent = buttonProps.icon;
                        return (
                          <Button 
                            variant={buttonProps.variant} 
                            size="sm" 
                            className={cn(buttonProps.className)}
                            onClick={handleConnect}
                            disabled={buttonProps.disabled || isSendingRequest}
                            title={buttonProps.disabled ? buttonProps.text : undefined}
                          >
                            {isSendingRequest ? (
                              <>
                                <Clock className="h-4 w-4 mr-1 animate-spin" />
                                Envoi...
                              </>
                            ) : (
                              <>
                                <IconComponent className="h-4 w-4 mr-1" />
                                {buttonProps.text}
                              </>
                            )}
                          </Button>
                        );
                     })()}
                  </>
                )}
                <Button variant="outline" size="sm" onClick={() => onViewProfile(member)}>
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Hover card pour informations détaillées */}
        {renderHoverCard()}
      </div>
    );
  }

  // Vue grille (grid view)
  return (
    <div 
      className="relative"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
    >
      <Card className="group hover:shadow-lg transition-all duration-300 overflow-hidden border-0 bg-background/50 backdrop-blur-sm min-h-[320px]">
        {/* Header avec photo, nom et statut - espace réservé pour les éléments absolus */}
        <div className="relative p-6 pt-14 pb-8">
          {/* Actions favoris/partage - positionnées en absolu avec espace réservé */}
          <div className="absolute top-4 left-4 z-10">
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 bg-white/90 hover:bg-white shadow-sm rounded-full" onClick={handleFavorite}>
                <Heart className={cn("h-3 w-3 text-black", isFavorited && "fill-red-500 text-red-500")} />
              </Button>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 bg-white/90 hover:bg-white shadow-sm rounded-full" onClick={() => onShare(member)}>
                <Share2 className="h-3 w-3 text-black" />
              </Button>
            </div>
          </div>

          {/* Badge de statut et bouton QR - positionnés en absolu */}
          <div className="absolute top-4 right-4 z-10 flex flex-col items-end gap-2">
            <PresenceIndicator 
              status={userStatus} 
              size="sm" 
              showLabel={true}
            />
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 w-7 p-0 bg-white/90 hover:bg-white shadow-sm rounded-full"
              onClick={(e) => {
                e.stopPropagation();
                setIsQRModalOpen(true);
              }}
              title="Voir la carte QR"
            >
              <QrCode className="h-3 w-3 text-black" />
            </Button>
          </div>

          {/* Photo et nom - espacement correct pour éviter les chevauchements */}
          <div className="flex items-start gap-4 mt-2">
            <EnhancedAvatar
              member={member}
              size="md"
              onClick={() => openZoom(member, allMembers, allMembers?.findIndex(m => m.id === member.id))}
              onMessage={onMessage}
              onConnect={onConnect}
              showHover={false}
              className="border-2 border-border flex-shrink-0"
            />
            
            <div className="flex-1 min-w-0 space-y-1">
              {/* Ligne 1 : Nom + Types discrets */}
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-lg text-black">{member.name}</h3>
                {member.is_team_member && currentUserId && <TeamMemberBadge size="sm" />}
                <MemberTypeBadgesLocal types={member.member_types || []} />
              </div>
              
              {/* Ligne 2 : RÔLE/POSTE en évidence */}
              <p className="text-sm text-muted-foreground font-medium">
                {formatMemberRoleByType(member)}
              </p>
              
              {/* Ligne 3 : Bio OU Hook contextuel (pas les deux) */}
              {contextualHook !== formatMemberRoleByType(member) && (
                <p className="text-xs text-foreground">
                  {contextualHook}
                </p>
              )}
            </div>
          </div>

        </div>

        <CardContent className="px-6 pb-6 space-y-4">
          {/* Informations membre */}
          <div className="space-y-2">
            <div className="flex items-center gap-4 text-xs text-black">
              <div className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                <span>{member.country}</span>
              </div>
              {lastActive && (
                <div className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  <span>{lastActive}</span>
                </div>
              )}
            </div>
            
            {/* Hot Lead Indicators - APRÈS localisation */}
            <HotLeadIndicators 
              member={member}
              userStatus={userStatus}
              compatibilityScore={compatibilityScore}
            />
            
            {/* Compétences matchées - seulement si pertinent */}
            {(currentUserMember?.skills?.length || currentUserMember?.needs?.length) && (
              <MatchedSkillsBadges 
                currentUserSkills={currentUserMember?.skills}
                currentUserNeeds={currentUserMember?.needs}
                memberSkills={member.skills}
                memberNeeds={member.needs}
                maxVisible={2}
              />
            )}
            
            {/* Social Proof compact */}
            <div className="flex gap-3 text-xs text-muted-foreground">
              {mutualConnections > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {mutualConnections} connexions
                </span>
              )}
              
              {hasPermission('view_analytics') && profileViews > 0 && (
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {profileViews} vues
                </span>
              )}
            </div>
          </div>

          {/* Product Interests */}
          {!interestsLoading && productInterests.length > 0 && (
            <ProductInterestsList 
              interests={productInterests}
              maxVisible={3}
              showLevels={false}
              compact={false}
            />
          )}

          {/* Ce que le membre RECHERCHE */}
          {member.needs && member.needs.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                <Search className="h-3.5 w-3.5" />
                <span>Recherche</span>
              </div>
              <div className="flex gap-1 flex-wrap">
                {member.needs.slice(0, 2).map((need, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs px-2 py-1 bg-yellow-100 text-yellow-900 border-yellow-300 font-medium">
                    {need}
                  </Badge>
                ))}
                {member.needs.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{member.needs.length - 2}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Ce que le membre PROPOSE */}
          {member.offers && member.offers.length > 0 && (
            <div className="space-y-1">
              <div className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                <Gift className="h-3.5 w-3.5" />
                <span>Propose</span>
              </div>
              <div className="flex gap-1 flex-wrap">
                {member.offers.slice(0, 2).map((offer, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs px-2 py-1 bg-yellow-100 text-yellow-900 border-yellow-300 font-medium">
                    {offer}
                  </Badge>
                ))}
                {member.offers.length > 2 && (
                  <Badge variant="outline" className="text-xs">
                    +{member.offers.length - 2}
                  </Badge>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          {isCurrentUser ? (
            <div className="w-full text-center py-2 px-4 bg-muted text-muted-foreground font-medium rounded-md text-sm">
              Mon profil
            </div>
          ) : (
            <>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" className="flex-1" onClick={() => onMessage(member)}>
                  <MessageCircle className="h-4 w-4 mr-1" />
                  Message
                </Button>
                {currentUserId && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setIsSuggestDialogOpen(true)}
                    className="gap-1.5 px-3"
                    title="Proposer ce profil à quelqu'un"
                  >
                    <Send className="h-3.5 w-3.5" />
                    <span className="text-xs">Proposer</span>
                  </Button>
                )}
               {(() => {
                 const buttonProps = getConnectButtonProps();
                 
                 if (buttonProps.showTwoButtons) {
                   const AcceptIcon = buttonProps.acceptButton.icon;
                   const DeclineIcon = buttonProps.declineButton.icon;
                   return (
                     <div className="flex gap-1 flex-1">
                       <Button 
                         variant={buttonProps.acceptButton.variant} 
                         size="sm" 
                         className={cn("flex-1", buttonProps.acceptButton.className)}
                         onClick={buttonProps.acceptButton.onClick}
                       >
                         <AcceptIcon className="h-4 w-4 mr-1" />
                         {buttonProps.acceptButton.text}
                       </Button>
                       <Button 
                         variant={buttonProps.declineButton.variant} 
                         size="sm" 
                         className={cn("flex-1", buttonProps.declineButton.className)}
                         onClick={buttonProps.declineButton.onClick}
                       >
                         <DeclineIcon className="h-4 w-4 mr-1" />
                         {buttonProps.declineButton.text}
                       </Button>
                     </div>
                   );
                 }
                 
                  const IconComponent = buttonProps.icon;
                  return (
                    <Button 
                      variant={buttonProps.variant} 
                      size="sm" 
                      className={cn("flex-1", buttonProps.className)}
                      onClick={handleConnect}
                      disabled={buttonProps.disabled || isSendingRequest}
                      title={buttonProps.disabled ? buttonProps.text : undefined}
                    >
                      {isSendingRequest ? (
                        <>
                          <Clock className="h-4 w-4 mr-1 animate-spin" />
                          Envoi...
                        </>
                      ) : (
                        <>
                          <IconComponent className="h-4 w-4 mr-1" />
                          {buttonProps.text}
                        </>
                      )}
                    </Button>
                 );
               })()}
              </div>

              <Button
            variant="ghost" 
            size="sm" 
            className="w-full bg-black text-yellow-400 hover:bg-yellow-400 hover:text-black transition-all duration-200"
            onClick={() => onViewProfile(member)}
          >
            Voir le profil complet
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Hover card pour informations détaillées */}
      {renderHoverCard()}
      
      {/* Photo Zoom Modal */}
      {selectedMember && (
        <PhotoZoomModal
          isOpen={isZoomOpen}
          onClose={closeZoom}
          member={selectedMember}
          allMembers={allMembers}
          currentIndex={currentIndex}
          onNavigate={(direction) => direction === 'next' ? navigateToNext() : navigateToPrev()}
          onMessage={onMessage}
          onConnect={onConnect}
          connectionStatus={connectionStatus}
        />
      )}

      {/* QR Code Modal */}
      <OtherMemberCardModal 
        member={member}
        open={isQRModalOpen}
        onOpenChange={setIsQRModalOpen}
      />

      {/* Suggest Profile Dialog */}
      <SuggestProfileDialog
        isOpen={isSuggestDialogOpen}
        onClose={() => setIsSuggestDialogOpen(false)}
        suggestedMember={member}
        allMembers={allMembers}
      />
    </div>
  );
};